import { useWidget } from './WidgetProvider';
import { RoomTypeStep } from './steps/RoomTypeStep';
import { ImageUploadStep } from './steps/ImageUploadStep';
import { SelectionsStep } from './steps/SelectionsStep';
import { ContactStep } from './steps/ContactStep';
import { PreviewTeaserStep } from './steps/PreviewTeaserStep';
import { PhoneGateStep } from './steps/PhoneGateStep';
import { VerifyCodeStep } from './steps/VerifyCodeStep';
import { ProjectDetailsStep } from './steps/ProjectDetailsStep';
import { ResultsStep } from './steps/ResultsStep';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, RefreshCw, Sparkles } from 'lucide-react';
import { ThemeToggleSimple } from '@/components/theme/ThemeToggle';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';

const STEP_PROGRESS: Record<string, number> = {
  room_select: 11.1,
  image_upload: 22.2,
  preview_teaser: 33.3,
  selections: 44.4,
  contact: 55.5,
  phone_gate: 66.6,
  verify_code: 77.7,
  project_details: 88.8,
  results: 100,
};

const STEP_TITLES: Record<string, string> = {
  room_select: 'Select Area',
  image_upload: 'Upload Photos',
  selections: 'Choose Renovations',
  contact: 'Your Information',
  preview_teaser: 'Your Design Preview',
  phone_gate: 'Verify Phone',
  verify_code: 'Enter Code',
  project_details: 'Project Details',
  results: 'Your Renovation Preview',
};

export function Widget() {
  const {
    currentStep,
    canGoPrev,
    prevStep,
    hasIncompleteSession,
    resumeSession,
    startFreshSession,
    tenantConfig,
  } = useWidget();

  const [showResumeDialog, setShowResumeDialog] = useState(hasIncompleteSession);

  const progress = STEP_PROGRESS[currentStep] || 0;
  const title = STEP_TITLES[currentStep] || '';

  const renderStep = () => {
    switch (currentStep) {
      case 'room_select':
        return <RoomTypeStep />;
      case 'image_upload':
        return <ImageUploadStep />;
      case 'selections':
        return <SelectionsStep />;
      case 'contact':
        return <ContactStep />;
      case 'preview_teaser':
        return <PreviewTeaserStep />;
      case 'phone_gate':
        return <PhoneGateStep />;
      case 'verify_code':
        return <VerifyCodeStep />;
      case 'project_details':
        return <ProjectDetailsStep />;
      case 'results':
        return <ResultsStep />;
      default:
        return <RoomTypeStep />;
    }
  };

  const showHeader = !['results'].includes(currentStep);
  const showProgress = !['results'].includes(currentStep);

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Resume Session Dialog */}
      <AlertDialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
        <AlertDialogContent className="max-w-[90vw] sm:max-w-md rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">
              Continue where you left off?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground">
              You have an incomplete design session. Would you like to continue or start fresh?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <AlertDialogCancel
              onClick={() => {
                startFreshSession();
                setShowResumeDialog(false);
              }}
              className="h-11 text-base"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Start Fresh
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                resumeSession();
                setShowResumeDialog(false);
              }}
              className="h-11 text-base bg-accent hover:bg-accent/90 text-accent-foreground"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header - Centered Layout */}
      {showHeader && (
        <header className="sticky top-0 z-10 bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 border-b border-border/40">
          <div className="container max-w-lg mx-auto px-4 py-3 sm:py-4 relative">
            {/* Back button - absolute top-left */}
            {canGoPrev && (
              <Button
                variant="ghost"
                size="icon"
                onClick={prevStep}
                className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full hover:bg-muted/80"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}

            {/* Theme toggle - absolute top-right */}
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <ThemeToggleSimple />
            </div>

            {/* Centered content - logo and title */}
            <div className="flex flex-col items-center text-center px-12">
              {tenantConfig && (
                <div className="mb-1">
                  {tenantConfig.branding?.logo_url ? (
                    <img
                      src={tenantConfig.branding.logo_url}
                      alt={tenantConfig.company_name}
                      className="h-6 sm:h-8 w-auto max-w-[140px] sm:max-w-[180px] object-contain mx-auto"
                    />
                  ) : (
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {tenantConfig.company_name}
                    </p>
                  )}
                </div>
              )}
              <h1 className="font-heading font-medium text-base truncate text-foreground">
                {title}
              </h1>
            </div>
          </div>
        </header>
      )}

      {/* Progress Bar */}
      {showProgress && (
        <div className="container max-w-lg mx-auto px-4 pt-3">
          <div className="relative">
            <Progress value={progress} className="h-1.5 bg-muted/50" />
            <div
              className="absolute top-0 left-0 h-1.5 rounded-full bg-gradient-to-r from-accent to-accent/80 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-right">
            Step {Math.ceil(progress / 11.1)} of 9
          </p>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 container max-w-lg mx-auto px-4 py-6 sm:py-8">
        {renderStep()}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/40 py-4 bg-muted/30">
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-accent" />
          <span>Powered by <span className="font-semibold text-foreground">RenovationAI</span></span>
        </div>
      </footer>
    </div>
  );
}
